// Global Instructions Rule Applied!
//
// Configuration → Agent Oversight service: the single aggregate admin read
// (GET /config/agent-access → view__agent_access_admin).
//
// No write lives here, but the screen is no longer read-only: the PROPOSAL
// verbs (approve / reject / retry / undo) post to the very same
// /agent/proposals routes the owner screen uses — the RPCs decide whether the
// caller may act, so there is no admin-specific write endpoint to add. Grants
// stay owner-managed; the admin's levers there remain the product kill switch
// and caps on the Agent Gateway panel.
//
// Same fetch conventions as agentGatewayConfigService. Admin-gated at the
// backend route AND inside the RPC.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AgentAccessAdminData,
  AgentActivityPage,
  AgentActivityQuery,
  AgentUsageAdmin,
  AgentCallInsights,
  AgentCallLogPage,
  AgentCallLogQuery,
  validateAgentCallInsights,
  validateAgentCallLogRows,
  validateAgentProposalAdminList,
  validateAgentUsageAdmin
} from '../../../state_manager/data_models/agent_access_admin_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchAgentAccessAdmin = async (
  accessToken: string | null,
  activityLimit = 50
): Promise<AgentAccessAdminData> => {
  const response = await fetch(
    `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_ACCESS_ADMIN)}?limit=${activityLimit}`,
    { method: API_ROUTES.CONFIG.AGENT_ACCESS_ADMIN.method, headers: authHeaders(accessToken) }
  )
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  const data = body.data || {}
  return {
    connections: data.connections || [],
    usage: data.usage || [],
    // Validated, unlike the two above: these rows feed a review form and an
    // approve that creates a real record, so a shape drift must fail here
    // rather than surface as a silently empty field on the form.
    recent_activity: validateAgentProposalAdminList(data.recent_activity || [])
  }
}

async function getJson(url: string, accessToken: string | null): Promise<Record<string, unknown>> {
  const response = await fetch(url, { method: 'GET', headers: authHeaders(accessToken) })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body as Record<string, unknown>
}

/** Usage over a period of UTC days (YYYY-MM-DD, inclusive; the server
 *  defaults to the last 7 days and clamps to 366). */
export const fetchAgentUsageAdmin = async (
  accessToken: string | null,
  period: { date_from?: string; date_to?: string }
): Promise<AgentUsageAdmin> => {
  const qs = new URLSearchParams()
  if (period.date_from) qs.set('date_from', period.date_from)
  if (period.date_to) qs.set('date_to', period.date_to)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = await getJson(
    `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_ACCESS_USAGE)}${suffix}`,
    accessToken
  )
  return validateAgentUsageAdmin(body.data)
}

/** One window of the Activity list (the list envelope). The rows are the
 *  proposal records the review verbs post back, so they are validated with
 *  the same strict schema as the owner screen's. */
export const fetchAgentProposalsAdmin = async (
  accessToken: string | null,
  query: AgentActivityQuery
): Promise<AgentActivityPage> => {
  const qs = new URLSearchParams()
  if (query.status) qs.set('status', query.status)
  if (query.grant_id) qs.set('grant_id', query.grant_id)
  if (query.tool) qs.set('tool', query.tool)
  if (query.live !== undefined) qs.set('live', String(query.live))
  if (query.search) qs.set('search', query.search)
  if (query.date_from) qs.set('date_from', query.date_from)
  if (query.date_to) qs.set('date_to', query.date_to)
  if (query.limit) qs.set('limit', String(query.limit))
  if (query.offset) qs.set('offset', String(query.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = await getJson(
    `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_ACCESS_PROPOSALS)}${suffix}`,
    accessToken
  )
  const count = body.count
  // A list envelope's count is a non-negative integer; anything else is a
  // malformed envelope, not something to coerce (internalAgentsService precedent).
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
    throw new Error('list envelope count is not a non-negative integer')
  }
  return {
    data: validateAgentProposalAdminList(body.data ?? []),
    has_more: body.has_more === true,
    count
  }
}

/** One window of the Call log (the list envelope). */
export const fetchAgentCallLogAdmin = async (
  accessToken: string | null,
  query: AgentCallLogQuery
): Promise<AgentCallLogPage> => {
  const qs = new URLSearchParams()
  if (query.grant_id) qs.set('grant_id', query.grant_id)
  if (query.tool) qs.set('tool', query.tool)
  if (query.kind) qs.set('kind', query.kind)
  if (query.outcomes?.length) qs.set('outcomes', query.outcomes.join(','))
  if (query.search) qs.set('search', query.search)
  if (query.date_from) qs.set('date_from', query.date_from)
  if (query.date_to) qs.set('date_to', query.date_to)
  if (query.limit) qs.set('limit', String(query.limit))
  if (query.offset) qs.set('offset', String(query.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = await getJson(
    `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_ACCESS_CALLS)}${suffix}`,
    accessToken
  )
  const count = body.count
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
    throw new Error('list envelope count is not a non-negative integer')
  }
  return { data: validateAgentCallLogRows(body.data ?? []), has_more: body.has_more === true, count }
}

/** Tool health, failures, argument usage, empty searches and scope refusals
 *  over a period of UTC days (YYYY-MM-DD, inclusive). */
export const fetchAgentCallInsights = async (
  accessToken: string | null,
  period: { date_from?: string; date_to?: string }
): Promise<AgentCallInsights> => {
  const qs = new URLSearchParams()
  if (period.date_from) qs.set('date_from', period.date_from)
  if (period.date_to) qs.set('date_to', period.date_to)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = await getJson(
    `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_ACCESS_CALL_INSIGHTS)}${suffix}`,
    accessToken
  )
  return validateAgentCallInsights(body.data)
}
